import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StateList } from '@core/constants/states';
import { NewProviderInformation } from '@core/model';
import { amountOfCharactersValidator, customMaskValidator, phoneNumberValidator } from '@shared/validators';
import { noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { ZIP_CODE_EXAMPLE, ZIP_CODE_REGEX } from '@shared/constants';
import { omit } from 'lodash-es';
import { distinctUntilChanged } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'new-provider',
    templateUrl: './new-provider.component.html',
    styleUrls: ['./new-provider.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NewProviderComponent implements OnInit, OnDestroy {
  @Input() panelHeader!: string;
  @Input() acknowledgementText!: string;
  @Input() defaultData?: NewProviderInformation;

  @Output() provider = new EventEmitter<NewProviderInformation | null>();

  form: FormGroup;

  readonly states = StateList;

  private subs = new SubSink();

  constructor(private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.form = this.createForm();

    this.subs.sink = this.form.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      const value = this.form.valid ? omit(this.form.value, 'acknowledged') as NewProviderInformation : null;

      this.provider.emit(value);
    });
  }

  reset(): void {
    this.form.reset();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      acknowledged: [!!this.defaultData, Validators.requiredTrue],
      fax: [this.defaultData?.fax, phoneNumberValidator],
      firstName: [this.defaultData?.firstName, [Validators.required, Validators.maxLength(50), noWhiteSpaceValidator]],
      lastName: [this.defaultData?.lastName, [Validators.required, Validators.maxLength(50), noWhiteSpaceValidator]],
      phone: [this.defaultData?.phone, phoneNumberValidator],
      physicalCity: [this.defaultData?.physicalCity, [noWhiteSpaceValidator, Validators.minLength(2), Validators.maxLength(50)]],
      physicalState: [this.defaultData?.physicalState],
      physicalStreet: [this.defaultData?.physicalStreet,[Validators.maxLength(100), noWhiteSpaceValidator]],
      physicalZip: [this.defaultData?.physicalZip, customMaskValidator(ZIP_CODE_REGEX, ZIP_CODE_EXAMPLE)],
      practiceNpinumber: [
        this.defaultData?.practiceNpinumber,
        [Validators.required, amountOfCharactersValidator(10)]
      ],
      practiceTaxId: [
        this.defaultData?.practiceTaxId,
        [Validators.required, amountOfCharactersValidator(9)]
      ],
    });
  }
}
